import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfigReader } from '../../../config/ConfigReader';
import { SiteCacheClient } from '../../../infrastructure/SiteCacheClient';
import { BatchClient, CommandName } from '../../../infrastructure/BatchClient';
import { Stage } from '../../../config/ApplicationConfig';

const useSiteCacheClient = () => {
  const config = ConfigReader.getInstance().getConfig();
  return new SiteCacheClient(config.apiUrl);
};

const useBatchClient = () => {
  const config = ConfigReader.getInstance().getConfig();
  return new BatchClient(config.apiUrl);
};

export const useSiteCache = () => {
  const client = useSiteCacheClient();
  const batchClient = useBatchClient();
  const queryClient = useQueryClient();
  const config = ConfigReader.getInstance().getConfig();

  const isGmcSyncDisabledForEnvironment =
    config.stage === Stage.LOCAL || config.stage === Stage.DEV || config.stage === Stage.STAGING;

  const invalidateCacheMutation = useMutation({
    mutationFn: () => client.invalidateCache(),
    onSuccess: () => {
      // Refetch invalidation status immediately after invalidating
      queryClient.invalidateQueries({ queryKey: ['site-cache', 'is-invalidating'] });
    },
  });

  const invalidateProductsByStatusMutation = useMutation({
    mutationFn: (publicationStatus?: 'draft') => client.invalidateProductsByStatus(publicationStatus),
  });

  const clearProductCacheBySKUsMutation = useMutation({
    mutationFn: (skus: string) => client.clearProductCacheBySKUs(skus),
  });

  const purgeCloudflareCacheMutation = useMutation({
    mutationFn: () => client.purgeCloudflareCache(),
    onSuccess: () => {
      // Refetch invalidation status immediately after purging
      queryClient.invalidateQueries({ queryKey: ['site-cache', 'is-invalidating'] });
    },
  });

  const isInvalidatingQuery = useQuery({
    queryKey: ['site-cache', 'is-invalidating'],
    queryFn: () => client.isInvalidating(),
    refetchInterval: (query) => {
      // Refetch every 2 seconds while invalidating, stop when not invalidating
      return query.state.data?.isInvalidating ? 2000 : false;
    },
  });

  const generateGoogleProductFeedMutation = useMutation({
    mutationFn: () => batchClient.triggerGenerateGoogleProductFeedJob(),
    onSuccess: () => {
      // Refetch running jobs immediately after triggering
      queryClient.invalidateQueries({ queryKey: ['gmc-product-feed', 'running-jobs'] });
    },
  });

  const runningGoogleProductFeedJobsQuery = useQuery({
    queryKey: ['gmc-product-feed', 'running-jobs'],
    queryFn: async () => {
      const response = await batchClient.listBatchJobs({
        commandName: CommandName.GENERATE_GOOGLE_PRODUCT_FEED,
      });
      const activeStatuses = ['SUBMITTED', 'PENDING', 'RUNNABLE', 'STARTING', 'RUNNING'];
      return response.jobs.filter((job) => activeStatuses.includes(job.status));
    },
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const lastGoogleProductFeedJobQuery = useQuery({
    queryKey: ['gmc-product-feed', 'last-job'],
    queryFn: async () => {
      const response = await batchClient.listBatchJobs({
        commandName: CommandName.GENERATE_GOOGLE_PRODUCT_FEED,
      });
      // Sort by creation date and get the most recent job
      const sortedJobs = response.jobs.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      return sortedJobs.length > 0 ? sortedJobs[0] : null;
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const generateMetaCatalogFeedMutation = useMutation({
    mutationFn: () => batchClient.triggerGenerateMetaCatalogFeedJob(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-catalog-feed', 'running-jobs'] });
    },
  });

  const runningMetaCatalogFeedJobsQuery = useQuery({
    queryKey: ['meta-catalog-feed', 'running-jobs'],
    queryFn: async () => {
      const response = await batchClient.listBatchJobs({
        commandName: CommandName.GENERATE_META_CATALOG_FEED,
      });
      const activeStatuses = ['SUBMITTED', 'PENDING', 'RUNNABLE', 'STARTING', 'RUNNING'];
      return response.jobs.filter((job) => activeStatuses.includes(job.status));
    },
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const lastMetaCatalogFeedJobQuery = useQuery({
    queryKey: ['meta-catalog-feed', 'last-job'],
    queryFn: async () => {
      const response = await batchClient.listBatchJobs({
        commandName: CommandName.GENERATE_META_CATALOG_FEED,
      });
      const sortedJobs = response.jobs.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      return sortedJobs.length > 0 ? sortedJobs[0] : null;
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  return {
    invalidateCache: invalidateCacheMutation.mutate,
    isInvalidatingCache: invalidateCacheMutation.isPending,
    isInvalidating: isInvalidatingQuery.data?.isInvalidating ?? false,
    isLoading: isInvalidatingQuery.isLoading,
    error: invalidateCacheMutation.error || isInvalidatingQuery.error,
    generateGoogleProductFeed: generateGoogleProductFeedMutation.mutate,
    isGeneratingGoogleProductFeed: generateGoogleProductFeedMutation.isPending,
    hasRunningGoogleProductFeedJob: Boolean(
      runningGoogleProductFeedJobsQuery.data && runningGoogleProductFeedJobsQuery.data.length > 0,
    ),
    googleProductFeedError: generateGoogleProductFeedMutation.error,
    isGmcSyncDisabledForEnvironment,
    lastGoogleProductFeedJob: lastGoogleProductFeedJobQuery.data,
    // New invalidation methods
    invalidateProductsByStatus: invalidateProductsByStatusMutation.mutate,
    isInvalidatingProductsByStatus: invalidateProductsByStatusMutation.isPending,
    invalidateProductsByStatusError: invalidateProductsByStatusMutation.error,
    clearProductCacheBySKUs: clearProductCacheBySKUsMutation.mutate,
    isClearingProductCacheBySKUs: clearProductCacheBySKUsMutation.isPending,
    clearProductCacheBySKUsError: clearProductCacheBySKUsMutation.error,
    purgeCloudflareCache: purgeCloudflareCacheMutation.mutate,
    isPurgingCloudflareCache: purgeCloudflareCacheMutation.isPending,
    purgeCloudflareCacheError: purgeCloudflareCacheMutation.error,
    generateMetaCatalogFeed: generateMetaCatalogFeedMutation.mutate,
    isGeneratingMetaCatalogFeed: generateMetaCatalogFeedMutation.isPending,
    hasRunningMetaCatalogFeedJob: Boolean(
      runningMetaCatalogFeedJobsQuery.data && runningMetaCatalogFeedJobsQuery.data.length > 0,
    ),
    metaCatalogFeedError: generateMetaCatalogFeedMutation.error,
    lastMetaCatalogFeedJob: lastMetaCatalogFeedJobQuery.data,
  };
};
